import {FC, useEffect, useMemo, useState} from 'react'
import {Field, FormikValues, Formik, Form, FieldMetaProps} from 'formik'
import {ListLoading} from './ListLoading'
import {ID, isNotEmpty} from '../_metronic/helpers'
import {FileUpload, ImageUpload, Switch} from './CustomInput'
import {Select, EmailCreatableSelect} from './Select'
import {MultiInput} from './MultiInput'
import {buildValidationSchema} from './validationSchema'
import {getObjectDifferences} from './compareObject'
import {useAuth} from '../app/modules/auth'
import {Editor} from './CustomInputJs'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {getValues} from './functions'
import {useTenantT} from '../i18n'
let db: any

export type OptionsQuery = {
  queryKey: string[]
  enabled?: boolean | string[]
  name?: string
  id?: string
  extras?: string[]
}

export type FieldProps = {
  name: string
  label?: string
  placeholder?: string
  helpText?: string
  required?: boolean //default is false
  full?: boolean //default is false
  disabled?: boolean //default is false
  type?: //default is text
  | 'time'
    | 'text'
    | 'date'
    | 'number'
    | 'email'
    | 'password'
    | 'textarea'
    | 'select'
    | 'switch'
    | 'file'
    | 'files'
    | 'multi-select'
    | 'multi-input'
    | 'checkbox'
    | 'image'
    | 'useremail'
    | 'link'
    | 'user_info'
    | 'school_info'
    | 'book_cover'
    | 'radio'
    | 'map'
    | 'list'
    | 'editor'
    | 'address'
  fileProps?: {
    limit?: number
    accept?: string | string[]
  }
  fieldProps?: {
    max?: number
  }
  options?: {label: string; value: any}[]
  optionsQuery?: OptionsQuery
  searchFn?: (val: string) => void
  createFn?: (val: {value: string}) => void
  selectFn?: (val: any) => void
  meta?: FieldMetaProps<any>
  truthValue?: string
  falseValue?: string
  view?: boolean //default is true
  edit?: boolean //default is true
  create?: boolean //default is true
  hidden?: boolean //default is false
  table?: boolean //default is false
  sortable?: boolean //default is false
  sortField?: string
  viewName?: string
  createField?: string
  fields?: FieldProps[]
  initialValue?: any // default for date is new Date(), for switch is true, for text is ''
  checkboxLabel?: string
  onchecked?: (e: React.ChangeEvent<HTMLInputElement>, formik: any) => void
  updateOnTable?: boolean
  filter?: number
  export?: boolean
  import?: boolean
  portal?: boolean
}

export type Props = {
  model?: string
  isLoading?: boolean
  data: any
  update?: (state: any) => Promise<any>
  create?: (state: any) => Promise<any>
  id?: ID | undefined
  setItemIdForUpdate?: (id: ID) => void
  refetch?: () => void
  fields: FieldProps[] | ((options: {values: any}) => FieldProps[])
  tabFields?: FieldTProps[]
  tab?: {
    steps: number
    step: number
    setStep: (step: number) => void
  }
  doNotResetOnSuccess?: boolean //default false
  //remove
  schema?: any
}
export type FieldTProps = {
  title: string
  fields: FieldProps[]
  component?: FC<any>
}

export const CForm: FC<Props> = (props) => {
  const [init, setInit] = useState(null)
  ///getting model from url which will be used as upload folder
  const getModelFromUrl = () => {
    const path = window.location.pathname
    const parts = path.split('/').filter(Boolean)
    return parts.includes('edit') || parts.includes('create')
      ? parts[parts.length - 2]
      : parts[parts.length - 1]
  }
  const modelName = getModelFromUrl()
  const STORAGE_KEY = `formValues_${modelName}_${props.id || 'new'}`

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) props.refetch?.()
    props.setItemIdForUpdate?.(undefined)

    let transaction = db?.transaction('formData', 'readwrite')
    const objectStore = transaction?.objectStore('formData')
    objectStore?.delete(STORAGE_KEY)
  }
  const submit = async (values: any, {setSubmitting, resetForm}: Partial<FormikValues>) => {
    setSubmitting(true)
    try {
      let val = {...values}
      val = await getObjectDifferences(props.model || modelName, values, props.data)
      delete val.idx
      if (isNotEmpty(values.id)) {
        await props.update?.({...val, id: values.id})
      } else await props.create?.(val)
      if (!props.doNotResetOnSuccess) resetForm()
      cancel(true)
    } catch (ex) {
      console.error(ex)
    } finally {
      setSubmitting?.(false)
    }
  }
  const {currentUser} = useAuth()
  const {schema, initialValues} = useMemo(
    () => {
      return buildValidationSchema(
        props.tabFields ? props.tabFields.flatMap((level) => level.fields) : props.fields,
        currentUser,
        props.data
      )
    },
    // eslint-disable-next-line
    []
  )
  useEffect(() => {
    const DBOpenRequest = window.indexedDB.open('formData', 1)
    // Register two event handlers to act on the database being opened successfully, or not
    DBOpenRequest.onerror = (event) => {}

    DBOpenRequest.onsuccess = (event) => {
      // Store the result of opening the database in the db variable. This is used a lot below
      db = DBOpenRequest.result
      const objectStore = db.transaction(['formData']).objectStore('formData').get(STORAGE_KEY)

      objectStore.onsuccess = (event: any) => {
        const result = event.target.result
        if (result) {
          setInit(result)
        }
      }
    }
    DBOpenRequest.onupgradeneeded = (event: any) => {
      db = event.target.result

      db.onerror = (event: any) => {}
      db.createObjectStore('formData', {keyPath: 'idx'})
    }
  }, [props.id, STORAGE_KEY])
  return (
    <Formik
      enableReinitialize={true}
      validationSchema={schema}
      initialValues={init || props.data || initialValues}
      onSubmit={submit}
    >
      {(formik) => {
        let fields = typeof props.fields === 'function' ? props.fields(formik) : props.fields
        if (props.tabFields) {
          const res: any[] = []
          fields?.forEach((level: any) => {
            const acc: any = []
            level.fields?.forEach((field: any) => {
              if (field.type === 'multi-input') {
                const flds = field.fields?.filter((f: any) =>
                  props.id ? f.edit !== false : f.create !== false
                )
                if (flds.length) {
                  acc.push({...field, fields: flds})
                }
              } else if (props.id ? field.edit !== false : field.create !== false) {
                acc.push(field)
              }
            })
            if (acc.length) {
              res.push({...level, fields: acc})
            }
          })
          fields = res
        } else {
          fields = fields?.filter((field) =>
            props.id ? field.edit !== false : field.create !== false
          )
        }
        if (props.tab) {
          fields = fields.map((field) => {
            if (field.fields) {
              return {
                ...field,
                fields: field.fields.map((f) => ({
                  ...f,
                  name: f.name.replace('idx', `${props.tab?.step}`),
                })),
              }
            }
            return {...field, name: field.name.replace('idx', `${props.tab?.step}`)}
          })
        }
        return (
          <CFormColumn
            props={props}
            fields={fields}
            formik={formik}
            currentUser={currentUser}
            cancel={cancel}
            STORAGE_KEY={STORAGE_KEY}
          />
        )
      }}
    </Formik>
  )
}
export const CFormColumn: FC<any> = ({props, fields, formik, currentUser, cancel, STORAGE_KEY}) => {
  // Save values to localStorage whenever they change
  useEffect(() => {
    let transaction = db?.transaction('formData', 'readwrite')
    const objectStore = transaction?.objectStore('formData')
    objectStore?.put({...formik.values, idx: STORAGE_KEY})
  }, [formik.values, STORAGE_KEY])
  return (
    <>
      <Form className='form' id='kt_modal_add_academic_year_form'>
        <div
          className='d-flex flex-column scroll-y me-n7 pe-7'
          id='kt_modal_add_academic_year_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_academic_year_header'
          data-kt-scroll-wrappers='#kt_modal_add_academic_year_scroll'
          data-kt-scroll-offset='300px'
        ></div>
        <div className='row'>
          <>
            {(props.tabFields ? fields[props.tab?.step || 0].fields : fields)?.map(
              (field: any, index: any) =>
                field.hidden === true ? null : field.type === 'multi-input' ? (
                  <MultiInput key={index} field={field} formik={formik} />
                ) : field.hidden ||
                  (currentUser?.role_id === 4 &&
                    ['class_id', 'section_id', 'student_id'].includes(field.name)) ? null : (
                  <CFields
                    key={index + '_' + (props.tab?.step || 0)}
                    field={field}
                    formik={formik}
                  />
                )
            )}
            {props.tab &&
              props.tab.step >= props.tab.steps &&
              Object.keys(formik.errors).length > 0 && (
                <div className='text-danger mb-2'>
                  {getValues(formik.errors).map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
          </>
        </div>
        {/* <div className='separator my-2'>
          <div className='row'>
            {props.tab &&
              props.tab.step >= props.tab.steps &&
              Object.keys(formik.errors).length > 0 &&
              JSON.stringify(formik.errors)}
          </div>
        </div> */}
        <div className='text-center pt-15'>
          {props.tab && props.tab.step > 0 && (
            <button
              type='button'
              onClick={() => props.tab?.setStep(props.tab.step - 1)}
              className='btn btn-light me-3'
            >
              <i className='fa-solid fa-angle-left mx-2 fs-5'></i> Previous
            </button>
          )}
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            data-kt-academic_years-modal-action='cancel'
            disabled={formik.isSubmitting || props.isLoading}
          >
            Discard
          </button>

          {props.tab && props.tab.step < props.tab.steps ? (
            <button
              type='button'
              className='btn btn-primary'
              onClick={() => props.tab?.setStep(props.tab.step + 1)}
            >
              <span className='indicator-label'>
                Next <i className='fa-solid fa-angle-right mx-2 fs-5'></i>
              </span>
            </button>
          ) : (
            <button
              type='button'
              className='btn btn-primary'
              data-kt-academic_years-modal-action='submit'
              disabled={
                props.isLoading || formik.isSubmitting || !formik.isValid || !formik.touched
              }
              data-kt-indicator={formik.isSubmitting ? 'on' : 'off'}
              onClick={() => formik.handleSubmit()}
            >
              <span className='indicator-label'>Submit</span>
              <span className='indicator-progress'>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            </button>
          )}
        </div>
      </Form>
      {(formik.isSubmitting || props.isLoading) && <ListLoading />}
    </>
  )
}

export const CFields: FC<{field: FieldProps; formik: any; idx?: number; data?: any}> = ({
  field: f,
  formik,
  idx,
}) => {
  const t = useTenantT()
  const field = {...f, label: t(f.label || ''), placeholder: t(f.placeholder || '')}
  const value = formik.getFieldMeta(field.name).value
  const error = formik.getFieldMeta(field.name).touched && formik.getFieldMeta(field.name).error

  useEffect(() => {
    if (field.type === 'date') {
      formik.setFieldValue(field.name, value ? new Date(value).toISOString().split('T')[0] : '')
    }
    // eslint-disable-next-line
  }, [value])

  return (
    <div className={'mb-5 fv-row ' + (!field.full ? 'col-lg-6' : '')}>
      {field.label && (
        <label className='d-flex align-items-center fs-6 fw-semibold form-label mb-2'>
          <span className={field.required ? 'required' : ''}>{field.label}</span>
          {field.helpText && (
            <OverlayTrigger
              placement='top'
              delay={{show: 250, hide: 500}}
              overlay={
                <Tooltip id='custom-tooltip'>
                  <span dangerouslySetInnerHTML={{__html: field.helpText}}></span>
                </Tooltip>
              }
            >
              <i className='fas fa-exclamation-circle ms-2 fs-7' data-bs-toggle='tooltip'></i>
            </OverlayTrigger>
          )}
        </label>
      )}
      {['select', 'multi-select', 'address'].includes(field.type!) ? (
        <Select
          {...field}
          placeholder={`Select ${field.placeholder || field.label}...`}
          onSelect={(val: any) => {
            if (val.__isNew__) field.createFn?.(val)
            else {
              let v
              if (field.type === 'multi-select') v = val.map((v: any) => v.value)
              else v = field.type === 'address' ? val.label : val.value
              field.selectFn
                ? field.selectFn({...val, fieldName: field.name})
                : formik.setFieldValue(field.name, v)
            }
          }}
          error={!!formik.getFieldMeta(field.name).error}
          formik={formik}
          idx={idx}
          value={value}
        />
      ) : field.type === 'useremail' ? (
        <EmailCreatableSelect
          name={field.name}
          placeholder={`Search ${field.placeholder || field.label}...`}
          formik={formik}
          selectFn={field.selectFn}
          createField={field.createField}
        />
      ) : field.type === 'file' ? (
        <FileUpload field={field} form={formik} />
      ) : field.type === 'image' ? (
        <ImageUpload field={field} form={formik} />
      ) : field.type === 'checkbox' ? (
        Array.isArray([]) && [].length > 0 ? (
          [].map((opt: any) => (
            <div key={opt.value} className='form-check form-check-custom form-check-solid mb-2'>
              <input
                type='checkbox'
                className='form-check-input'
                id={`${field.name}_${opt.value}`}
                name={field.name}
                value={opt.value}
                checked={formik.values[field.name]?.includes(opt.value)}
                onChange={() => {
                  const selected: any[] = formik.values[field.name] || []
                  if (selected.includes(opt.value)) {
                    formik.setFieldValue(
                      field.name,
                      selected.filter((v) => v !== opt.value)
                    )
                  } else {
                    formik.setFieldValue(field.name, [...selected, opt.value])
                  }
                }}
              />
              <label className='form-check-label' htmlFor={`${field.name}_${opt.value}`}>
                {opt.label}
              </label>
            </div>
          ))
        ) : (
          <div className='form-check form-check-custom form-check-solid'>
            <input
              type='checkbox'
              className='form-check-input'
              id={field.name}
              name={field.name}
              checked={formik.values[field.name] || false}
              onChange={(e) => {
                formik.setFieldValue(field.name, e.target.checked)
                if (typeof field.onchecked === 'function') {
                  field.onchecked(e, formik)
                }
              }}
            />
          </div>
        )
      ) : field.type === 'switch' ? (
        <Switch
          field={field}
          value={value}
          disabled={field.disabled}
          onChange={(val: any) =>
            field.selectFn
              ? field.selectFn({value: val.target.checked, fieldName: field.name})
              : formik.setFieldValue(field.name, val.target.checked)
          }
        />
      ) : (
        <Field
          type={field.type}
          className='form-control form-control-solid'
          component={field.type === 'editor' ? Editor : ''}
          name={field.name}
          as={field.type === 'textarea' ? 'textarea' : undefined}
          disabled={field.disabled}
          placeholder={field.placeholder || field.label}
          {...(field.fieldProps || {})}
        />
      )}
      {error && <div className='text-danger mt-2'>{error}</div>}
    </div>
  )
}
